import { Category, Module, Zone, StoreType } from "@apis/index";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import Row from "@components/row";
import Wrapper from "@components/wrapper";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormSection, UploadSection } from "./styles";

const SubCategoryForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<any[]>([]);
  const [storeTypes, setStoreTypes] = useState<any[]>([]);

  const fetchSubCategories = async () => {
    await Category.list({
      state: "subcategories",
      params: { page: 1, limit: 30 },
    });
  };

  const fetchCategories = async () => {
    const res: any = await Category.list({
      params: { parentId: 0, limit: 5000 },
    });
    const items = res?.items || [];
    const mappedCategories = items.map((cat: any) => ({
      ...cat,
      displayName: cat.module?.name
        ? `${cat.name} (${cat.module.name})`
        : cat.name,
    }));
    setCategories(mappedCategories);
  };

  const fetchStoreTypes = async (moduleId?: any) => {
    if (!moduleId) {
      setStoreTypes([]);
      return;
    }
    const res: any = await StoreType.list({ params: { moduleId, limit: 1000 } });
    setStoreTypes(Array.isArray(res) ? res : res?.data || []);
  };

  const [initialValues, setInitialValues] = useState<any>({
    subcategory: "",
    category: "",
    image: "",
    commission: { type: "percent", value: "" },
    typeId: "",
  });

  useEffect(() => {
    if (!!state || state?.id) {
      setInitialValues({
        subcategory: state.name,
        category: state?.parent?.id,
        image: state.icon,
        commission:
          typeof state.commission === "object"
            ? state.commission
            : { type: "percent", value: state.commission || "" },
        typeId: state?.typeId || state?.type?.id || "",
      });
      if (state?.parent?.id) {
        const parentCat = categories.find((c: any) => c.id === state.parent.id);
        if (parentCat?.module?.id) {
          fetchStoreTypes(parentCat.module.id);
        }
      }
    } else {
      setInitialValues({
        subcategory: "",
        category: "",
        image: "",
        commission: { type: "percent", value: "" },
        typeId: "",
      });
    }
  }, [state, categories]);

  const createFormData = (values) => {
    const formData = new FormData();
    formData.append("name", values.subcategory);
    formData.append("parentId", values.category);
    if (values.image?.rawFile) {
      formData.append("icon", values.image.rawFile);
    }
    if (values.commission) {
      formData.append("commission", JSON.stringify(values.commission));
    }
    if (values.typeId) {
      formData.append("typeId", values.typeId);
    }
    return formData;
  };

  const handleSubmit = async (values: typeof initialValues, { resetForm }) => {
    const formData: any = createFormData(values);

    if (state?.id) {
      // ✅ Update existing subcategory
      await Category.update({
        state: "subcategories",
        data: formData,
        id: state?.id,
      }).then(async () => {
        await fetchSubCategories();
        resetForm(); // ✅ Ensure reset before navigating
        navigate("/admin/product-management/categories?tab=Sub-Category");
      });
    } else {
      // ✅ Create new subcategory
      await Category.create({ state: "subcategories", data: formData });
      resetForm(); // ✅ Proper reset after successful creation
      setInitialValues({
        subcategory: "",
        category: "",
        image: "",
        commission: { type: "percent", value: "" },
        typeId: "",
      }); // ✅ Also reset local state so Formik reinitializes cleanly
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <Wrapper stylewrapper={`border-radius:12px`}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Row>
              <FormSection>
                <Column>
                  <Row>
                    <Fields.DropDown
                      hasSearch
                      name="category"
                      label="Category"
                      placeholder="---Select Category---"
                      options={categories}
                      titleAsKey="displayName"
                      valueAsKey="id"
                      styleInput="width:96%"
                      styleOptionsWrapper="width:100%"
                      onChange={(val) => {
                        setFieldValue("category", val);
                        setFieldValue("typeId", "");
                        const selectedCat = categories.find(
                          (c: any) => c.id === val,
                        );
                        if (selectedCat?.module?.id) {
                          fetchStoreTypes(selectedCat.module.id);
                        }
                      }}
                    />
                    <Fields.Input
                      name="subcategory"
                      label="Sub-Category"
                      placeholder="Enter Sub-Category"
                    />
                    <div style={{ width: "100%" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#374151",
                        }}
                      >
                        Commission
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "stretch",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          overflow: "hidden",
                          height: "48px",
                        }}
                      >
                        <div
                          style={{
                            flex: "0 0 60px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f3f4f6",
                            borderRight: "1px solid #e5e7eb",
                            cursor: "pointer",
                            fontWeight: 600,
                            color:
                              values.commission?.type === "percent"
                                ? "#3b82f6"
                                : "#6b7280",
                            transition: "all 0.2s",
                          }}
                          onClick={() => {
                            const currentType =
                              values.commission?.type || "percent";
                            const newType =
                              currentType === "percent" ? "flat" : "percent";
                            setFieldValue("commission", {
                              ...values.commission,
                              type: newType,
                            });
                          }}
                          title="Click to switch type"
                        >
                          {values.commission?.type === "percent" ? "%" : "Flat"}
                        </div>
                        <div style={{ flex: 1 }}>
                          <Fields.Input
                            name="commission.value"
                            label=""
                            placeholder={
                              values.commission?.type === "percent"
                                ? "10"
                                : "50"
                            }
                            type="number"
                            styleInput="border: none; margin: 0; padding: 0 16px; height: 100%; width: 100%; font-size: 16px; outline: none;"
                            styleWrapper="margin: 0; padding: 0; border: none; height: 100%;"
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ width: "100%" }}>
                      <Fields.DropDown
                        name="typeId"
                        label="Store Type"
                        placeholder="---Select Store Type---"
                        options={storeTypes}
                        titleAsKey="name"
                        valueAsKey="id"
                        styleInput="width:96%"
                        styleOptionsWrapper="width:100%"
                        disabled={!values.category}
                      />
                    </div>
                  </Row>
                </Column>
              </FormSection>

              <UploadSection>
                <Fields.Upload name="image" height={200} />
              </UploadSection>
            </Row>
            <br />
            <ButtonWrapper>
              <Button title="Reset" type="reset" variant="reset" />
              <Button title="Save" type="submit" />
            </ButtonWrapper>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

export default SubCategoryForm;
